import React from "react";
import { Stack, Grid, Typography, Box } from "@mui/material";
import LineGrid from "./LineGrid";
import EditorPickCard from "./EditorPickCard";
import SectionDescriptionCard from "./SectionDescriptionCard";
import MarqueeContainer from "../../components/marquee/MarqueeContainer";
import AvatarOnlyCard from "./AvatarOnlyCard";

/**
 * MagazineSectionsBlock
 * @param {{ sections: any[], threadInfoById: Record<string, any>, userInfoById: Record<string, any>, collectionInfoById: Record<string, any>, isMobile: boolean, issueId: string, videoCacheInfo: any[] }} props
 */
const MagazineSectionsBlock = ({
	sections,
	threadInfoById,
	userInfoById,
	collectionInfoById,
	isMobile,
	issueId,
	videoCacheInfo = [],
	sectionIndexOffset = 0,
	addTopBorder = false,
}) => {
	if (!sections || sections.length === 0) {
		// 섹션이 아예 없을 때 플레이스홀더 1섹션(3개) 표시
		const placeholderSection = [
			{
				id: "ep-ph-sec-1",
				title: "Editors Pick",
				threadIdList: ["ph-1", "ph-2", "ph-3"],
			},
		];
		sections = placeholderSection;
	}
	return (
		<Stack spacing={0} sx={addTopBorder ? { borderTop: '4px solid', borderColor: 'primary.main' } : {}}>
			{sections
				.slice()
				.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
				.map((sec, index) => {
					// 실제 스레드 수집 (부모에서 순서 결정됨)
					let secThreads = (sec.threadIdList || [])
						.map((id) => threadInfoById[String(id)])
						.filter(Boolean);
					if (secThreads.length < 3) {
						const need = 3 - secThreads.length;
						const placeholders = Array.from({ length: need }, (_, i) => ({
							id: `ep-ph-${index}-${i + 1}`,
							text: "Placeholder",
						}));
						secThreads = [...secThreads, ...placeholders];
					}
					const sectionVideoCache = videoCacheInfo[index] || null;

					// 섹션의 유저 아바타 정보 추출 (MagazineIssue와 완전히 동일한 방식)
					const sectionUsers = [];
					const uniqueUserIds = new Set();

					secThreads.forEach((thread) => {
						const collection = collectionInfoById[thread.collectionId];
						const userId =
							collection?.userID || collection?.userId || collection?.user_id;

						if (userId && !uniqueUserIds.has(userId)) {
							uniqueUserIds.add(userId);
							const user = userInfoById[userId] || { id: userId };

							// Firebase Storage URL만 사용 (Instagram CDN/Threads API URL 제외)
							const fallbackAvatar = collection?.profileImage?.includes?.(
								"firebasestorage.googleapis.com"
							)
								? collection.profileImage
								: null;

							// username 찾기
							const username =
								user?.profile?.username ||
								user?.username ||
								collection?.profile?.username ||
								collection?.username ||
								"";

							sectionUsers.push({
								id: userId,
								// avatarUrl은 사용하지 않음 - Threads API URL은 만료되므로
								fallbackAvatar: fallbackAvatar, // collection에 저장된 URL만 사용
								username: username,
							});
						}
					});

					// 마퀴에 표시할 텍스트를 단어 단위로 분리
					const marqueeText = sec.description || sec.title || "섹션";
					const words = marqueeText
						.split(" ")
						.filter((word) => word.length > 0);

					// 텍스트와 아바타를 교차 배치: [단어, 아바타, 단어, 아바타, ...]
					const marqueeContent = [];
					const fontSize = isMobile ? "h5" : "h4";
					const repeatCount = isMobile ? 12 : 24; // 충분히 긴 길이 확보로 빈 공간 방지

					for (let repeat = 0; repeat < repeatCount; repeat++) {
						let avatarIndex = 0; // 각 반복마다 독립적인 인덱스
						words.forEach((word, wordIdx) => {
							// 단어 추가
							marqueeContent.push(
								<Typography
									key={`word-${sec.id}-${repeat}-${wordIdx}`}
									variant={fontSize}
									sx={{
										fontFamily:
											'"source-han-serif-korean", "Noto Serif KR", serif',
										fontWeight: 900,
										color: "primary.main",
										whiteSpace: "nowrap",
										px: isMobile ? 0 : 0.5,
										fontSize: isMobile ? "1rem" : undefined,
										userSelect: "none",
										letterSpacing: "-0.02em",
										display: "inline-flex",
										alignItems: "center",
										maxWidth: "100%",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{word}
								</Typography>
							);

							// 각 단어 뒤에 아바타 1개 교차 배치 (라운드로빈)
							if (sectionUsers.length > 0) {
								const user = sectionUsers[avatarIndex % sectionUsers.length];
								avatarIndex += 1;
								if (user) {
									const userObj = {
										id: user.id,
										profile: {
											username: user.username,
											name: user.username,
										},
									};
									marqueeContent.push(
										<Box
											key={`avatar-${sec.id}-${repeat}-${wordIdx}`}
											sx={{
												display: "inline-flex",
												alignItems: "center",
												px: isMobile ? 0 : 0.5,
											}}
										>
											<AvatarOnlyCard
												user={userObj}
												isMobile={isMobile}
												fallbackAvatarUrl={user.fallbackAvatar}
												size={isMobile ? 28 : 56}
											/>
										</Box>
									);
								}
							}
						});
					}

					return (
						<Stack
							key={sec.id}
							spacing={0}
							sx={{
								...(sec !== sections[sections.length - 1]
									? { borderBottom: "4px solid", borderColor: "primary.main" }
									: {}),
							}}
							data-section-index={index + sectionIndexOffset} // Intersection Observer를 위한 속성 (글로벌 오프셋 적용)
						>
							<LineGrid
								gap={0}
								borderColor="primary.main"
								gridColumns={isMobile ? 2 : 4}
							>
								{/* 섹션 설명 카드 */}
								<Grid
									size={{ xs: 6, sm: 6, md: 4, lg: 4 }}
									sx={{
										minHeight: isMobile ? "324px" : "auto",
										overflow: "hidden",
										maxWidth: "100%",
									}}
								>
									<SectionDescriptionCard
										title={sec.title || "섹션"}
										description={sec.description}
										isMobile={isMobile}
										issueId={issueId}
										sectionIndex={index + sectionIndexOffset}
										sectionThreads={secThreads}
										userInfoById={userInfoById}
										collectionInfoById={collectionInfoById}
										videoCacheInfo={sectionVideoCache}
									/>
								</Grid>
								{/* 에디터 픽 카드들 */}
								{secThreads.map((th) => {
									// 해당 스레드의 유저 정보 추출 (MagazineSectionsBlock과 동일한 로직)
									const collection = collectionInfoById[th.collectionId];
									const userId =
										collection?.userID ||
										collection?.userId ||
										collection?.user_id;
									const user = userInfoById[userId] || { id: userId };

									// Firebase Storage URL만 사용 (Instagram CDN/Threads API URL 제외)
									const fallbackAvatar = collection?.profileImage?.includes?.(
										"firebasestorage.googleapis.com"
									)
										? collection.profileImage
										: null;

									// username 찾기
									const username =
										user?.profile?.username ||
										user?.username ||
										collection?.profile?.username ||
										collection?.username ||
										"";

									const name =
										user?.profile?.name ||
										user?.name ||
										collection?.profile?.name ||
										collection?.name ||
										username;

									return (
										<Grid
											size={{ xs: 6, sm: 6, md: 8 / 3, lg: 8 / 3 }}
											sx={{
												minHeight: isMobile ? "324px" : "auto",
												overflow: "hidden",
												maxWidth: "100%",
											}}
											key={th.id}
										>
											<EditorPickCard
												thread={th}
												isMobile={isMobile}
												userID={userId}
												username={username}
												name={name}
												profileImage={fallbackAvatar}
											/>
										</Grid>
									);
								})}
							</LineGrid>

							{/* 섹션 하단 마퀴 텍스트 */}
							{/* <Box
								sx={{
									borderTop: "2px solid",
									borderColor: "primary.main",
									overflow: "hidden",
									opacity: 0.25,
									transition: "opacity 0.3s ease-in-out",
									"&:hover": {
										opacity: 1,
									},
								}}
							>
								<MarqueeContainer
									speed={0.8}
									duplicate={2}
									height={isMobile ? 40 : 90}
									reverse={index % 2 === 1}
									sx={{
										display: "flex",
										alignItems: "center",
									}}
								>
									{marqueeContent}
								</MarqueeContainer>
							</Box> */}
						</Stack>
					);
				})}
		</Stack>
	);
};

export default MagazineSectionsBlock;
